import React, { useState } from 'react';
import '../styles/TaskDialog.css';
import { supabase } from '../config/supabase';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/api';

function TaskDialog({ task, onClose, onTaskClaimed }) {
  const [solutionText, setSolutionText] = useState('');
  const [githubPrUrl, setGithubPrUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');

  const handleClaim = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Check if Supabase is configured
      if (!supabase) {
        setError('Authentication is not configured');
        return;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to claim a task');
        return;
      }

      // Call backend API to claim task
      const response = await fetchWithAuth(`${API_BASE_URL}/tasks/${task.id}/claim`, {
        method: 'POST',
        body: JSON.stringify({
          worker_id: user.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to claim task');
      }

      alert('Task claimed successfully!');
      onTaskClaimed();
    } catch (err) {
      console.error('Error claiming task:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartTask = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Check if Supabase is configured
      if (!supabase) {
        setError('Authentication is not configured');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in');
        return;
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/tasks/${task.id}/start`, {
        method: 'POST',
        body: JSON.stringify({
          worker_id: user.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start task');
      }

      alert('Task started! You can now work on it.');
      onTaskClaimed();
    } catch (err) {
      console.error('Error starting task:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!solutionText.trim()) {
      setError('Please provide a solution description');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Check if Supabase is configured
      if (!supabase) {
        setError('Authentication is not configured');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in');
        return;
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/tasks/${task.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({
          worker_id: user.id,
          solution_text: solutionText,
          github_pr_url: githubPrUrl || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit solution');
      }

      alert('Solution submitted successfully!');
      onTaskClaimed();
    } catch (err) {
      console.error('Error submitting solution:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canClaim = task.status === 'available';
  const canStart = task.status === 'claimed';
  const canSubmit = ['claimed', 'in_progress', 'rejected'].includes(task.status);

  return (
    <div className="task-dialog-overlay" onClick={onClose}>
      <div className="task-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{task.title}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="task-tabs">
          <button
            className={`tab ${activeTab === 'description' ? 'active' : ''}`}
            onClick={() => setActiveTab('description')}
          >
            Description
          </button>
          <button
            className={`tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          {canSubmit && (
            <button
              className={`tab ${activeTab === 'submit' ? 'active' : ''}`}
              onClick={() => setActiveTab('submit')}
            >
              Submit Solution
            </button>
          )}
          {task.acceptance_criteria && task.acceptance_criteria.length > 0 && (
            <button
              className={`tab ${activeTab === 'criteria' ? 'active' : ''}`}
              onClick={() => setActiveTab('criteria')}
            >
              Acceptance Criteria
            </button>
          )}
        </div>

        <div className="dialog-content">
          {activeTab === 'description' && (
            <div className="tab-content">
              <h3>Task Description</h3>
              <p className="task-full-description">{task.description}</p>

              <div className="task-info-grid">
                <div className="info-item">
                  <span className="info-label">Priority:</span>
                  <span className={`info-value priority-${task.priority}`}>{task.priority}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Complexity:</span>
                  <span className="info-value">{task.complexity}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Bounty:</span>
                  <span className="info-value bounty">${task.bounty_portion?.toFixed(2)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value">{task.status.replace('_', ' ')}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="tab-content">
              <h3>Project Details</h3>
              <div className="project-details">
                <div className="detail-item">
                  <strong>Project:</strong> {task.problem?.title}
                </div>
                <div className="detail-item">
                  <strong>Category:</strong> {task.problem?.category}
                </div>
                <div className="detail-item">
                  <strong>Project Description:</strong> {task.problem?.description}
                </div>
                {task.estimated_hours && (
                  <div className="detail-item">
                    <strong>Estimated Hours:</strong> {task.estimated_hours}
                  </div>
                )}
                {task.assigned_worker && (
                  <div className="detail-item">
                    <strong>Assigned to:</strong> {task.assigned_worker.username || task.assigned_worker.email}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'criteria' && (
            <div className="tab-content">
              <h3>Acceptance Criteria</h3>
              <ul className="acceptance-criteria-list">
                {(task.acceptance_criteria || []).map((criterion, index) => (
                  <li key={index}>{criterion}</li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'submit' && (
            <div className="tab-content">
              <h3>Submit Your Solution</h3>
              <div className="solution-form">
                <div className="form-group">
                  <label>Solution Description *</label>
                  <textarea
                    value={solutionText}
                    onChange={(e) => setSolutionText(e.target.value)}
                    placeholder="Describe your solution, what you implemented, and how it meets the acceptance criteria..."
                    rows="10"
                  />
                </div>

                <div className="form-group">
                  <label>GitHub PR URL (optional)</label>
                  <input
                    type="url"
                    value={githubPrUrl}
                    onChange={(e) => setGithubPrUrl(e.target.value)}
                    placeholder="https://github.com/..."
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="dialog-footer">
          <button className="cancel-button" onClick={onClose}>
            Close
          </button>
          {canClaim && (
            <button
              className="primary-button"
              onClick={handleClaim}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Claiming...' : 'Claim This Task'}
            </button>
          )}
          {canStart && (
            <button
              className="primary-button"
              onClick={handleStartTask}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Starting...' : 'Start Working'}
            </button>
          )}
          {canSubmit && activeTab === 'submit' && (
            <button
              className="primary-button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Solution'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskDialog;
